import React, { useState } from 'react';
import { AlertTriangle, X, Cloud, Database, FolderOpen, HardDrive, Trash2, Loader2 } from 'lucide-react';
import { useToast } from '../context/ToastContext';

interface DeleteWorkspaceModalProps {
  open: boolean;
  workspace: {
    id: number;
    name: string;
    domain: string;
  } | null;
  onClose: () => void;
  onDelete: (workspaceId: number) => Promise<void>;
}

const DeleteWorkspaceModal: React.FC<DeleteWorkspaceModalProps> = ({
  open,
  workspace,
  onClose,
  onDelete,
}) => {
  const { showSuccess, showError } = useToast();
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [cancelHovered, setCancelHovered] = useState(false);
  const [deleteHovered, setDeleteHovered] = useState(false);

  const handleDelete = async () => {
    if (!workspace) return;

    setLoading(true);

    try {
      await onDelete(workspace.id);
      showSuccess('Workspace deleted successfully');
      handleClose();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to delete workspace');
      setLoading(false);
    }
  };

  const handleClose = () => {
    setConfirmText('');
    setLoading(false);
    onClose();
  };

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (confirmText === '' || isConfirmValid) {
      e.target.style.borderColor = 'var(--v2-primary)';
      e.target.style.boxShadow = '0 0 0 3px var(--v2-primary-muted)';
    }
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (confirmText === '' || isConfirmValid) {
      e.target.style.borderColor = 'var(--v2-border)';
    }
    e.target.style.boxShadow = 'none';
  };

  const isConfirmValid = confirmText === workspace?.domain;

  if (!open || !workspace) return null;

  const deletionItems = [
    { icon: Cloud, label: 'All running services and pods' },
    { icon: Database, label: 'The entire database and all data' },
    { icon: FolderOpen, label: 'All configuration files' },
  ];

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        style={{
          backgroundColor: 'var(--v2-surface)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-lg)',
          border: '1px solid var(--v2-border)',
          maxWidth: '500px',
          width: '90%',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: 'var(--font-sans)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid var(--v2-border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--v2-error-bg)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AlertTriangle size={20} strokeWidth={1.5} color="var(--v2-error)" />
            </div>
            <div>
              <h2
                style={{
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  color: 'var(--v2-text)',
                  margin: 0,
                  letterSpacing: '-0.025em',
                }}
              >
                Delete Workspace
              </h2>
              <p
                style={{
                  fontSize: '0.875rem',
                  color: 'var(--v2-error)',
                  margin: '2px 0 0 0',
                  fontWeight: 500,
                }}
              >
                This action cannot be undone
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              padding: '8px',
              cursor: 'pointer',
              borderRadius: 'var(--radius-sm)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--v2-text-muted)',
              transition: 'all 0.15s',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--v2-surface-hover)';
              e.currentTarget.style.color = 'var(--v2-text)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'var(--v2-text-muted)';
            }}
          >
            <X size={20} strokeWidth={1.5} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
          {/* Workspace Info */}
          <div
            style={{
              backgroundColor: 'var(--v2-surface-alt)',
              border: '1px solid var(--v2-border)',
              borderRadius: 'var(--radius-md)',
              padding: '16px',
              marginBottom: '24px',
            }}
          >
            <p
              style={{
                fontSize: '0.6875rem',
                fontWeight: 600,
                color: 'var(--v2-text-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                margin: '0 0 6px 0',
              }}
            >
              Workspace to Delete
            </p>
            <p
              style={{
                fontSize: '1.0625rem',
                fontWeight: 600,
                color: 'var(--v2-text)',
                margin: '0 0 4px 0',
              }}
            >
              {workspace.name}
            </p>
            <p
              style={{
                fontSize: '0.875rem',
                color: 'var(--v2-text-tertiary)',
                margin: 0,
                fontFamily: 'var(--font-mono)',
              }}
            >
              {workspace.domain}.dashboardfox.app
            </p>
          </div>

          {/* What will be deleted */}
          <div style={{ marginBottom: '24px' }}>
            <p
              style={{
                fontSize: '0.6875rem',
                fontWeight: 600,
                color: 'var(--v2-text-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                margin: '0 0 12px 0',
              }}
            >
              This will permanently delete
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {deletionItems.map((item, idx) => {
                const Icon = item.icon;
                return (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div
                      style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: 'var(--radius-sm)',
                        backgroundColor: 'var(--v2-error-bg)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon size={14} strokeWidth={1.5} color="var(--v2-error)" />
                    </div>
                    <span style={{ fontSize: '0.875rem', color: 'var(--v2-text)' }}>{item.label}</span>
                  </div>
                );
              })}
            </div>

            {/* Backup note */}
<div
              style={{
                backgroundColor: 'var(--v2-error-bg)',
                border: '1px solid #fca5a5',
                borderRadius: 'var(--radius-md)',
                padding: '12px 14px',
                marginTop: '16px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
              }}
            >
              <div
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: 'var(--radius-sm)',
                  backgroundColor: 'rgba(239, 68, 68, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <AlertTriangle size={14} strokeWidth={1.5} color="var(--v2-error)" />
              </div>
              <div>
                <p style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--v2-error)', margin: '0 0 2px 0' }}>
                  This action is permanent and irrevocable
                </p>
                <p style={{ fontSize: '0.8125rem', color: 'var(--v2-error)', margin: 0 }}>
                  All data will be permanently destroyed with no possibility of recovery
                </p>
              </div>
            </div>
  </div>
          {/* Confirmation Input */}
          <div>
            <label
              style={{
                display: 'block',
                fontSize: '0.875rem',
                color: 'var(--v2-text)',
                marginBottom: '8px',
              }}
            >
              Type{' '}
              <strong
                style={{
                  fontFamily: 'var(--font-mono)',
                  backgroundColor: 'var(--v2-bg)',
                  padding: '2px 6px',
                  borderRadius: '4px',
                }}
              >
                {workspace.domain}
              </strong>{' '}
              to confirm deletion:
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={workspace.domain}
              disabled={loading}
              style={{
                width: '100%',
                padding: '10px 12px',
                backgroundColor: 'var(--v2-surface)',
                border: `1px solid ${confirmText !== '' && !isConfirmValid ? 'var(--v2-error)' : 'var(--v2-border)'}`,
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.875rem',
                color: 'var(--v2-text)',
                fontFamily: 'var(--font-mono)',
                outline: 'none',
                transition: 'border-color 0.15s, box-shadow 0.15s',
                boxSizing: 'border-box',
              }}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
            />
            {confirmText !== '' && !isConfirmValid && (
              <p style={{ fontSize: '0.8125rem', color: 'var(--v2-error)', margin: '6px 0 0 0' }}>
                Domain name does not match
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid var(--v2-border)',
            backgroundColor: 'var(--v2-surface-alt)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
          }}
        >
          <button
            onClick={handleClose}
            disabled={loading}
            onMouseEnter={() => setCancelHovered(true)}
            onMouseLeave={() => setCancelHovered(false)}
            style={{
              padding: '10px 20px',
              backgroundColor: cancelHovered && !loading ? 'var(--v2-surface-hover)' : 'var(--v2-surface)',
              color: cancelHovered && !loading ? 'var(--v2-text)' : 'var(--v2-text-tertiary)',
              border: '1px solid var(--v2-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.5 : 1,
              transition: 'all 0.15s',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={!isConfirmValid || loading}
            onMouseEnter={() => setDeleteHovered(true)}
            onMouseLeave={() => setDeleteHovered(false)}
            style={{
              padding: '10px 20px',
              backgroundColor:
                !isConfirmValid || loading
                  ? '#fecaca'
                  : deleteHovered
                    ? '#b91c1c'
                    : 'var(--v2-error)',
              color: !isConfirmValid || loading ? '#f87171' : '#ffffff',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: !isConfirmValid || loading ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.15s',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            {loading ? (
              <>
                <Loader2 size={16} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 size={16} strokeWidth={1.5} />
                Delete Workspace
              </>
            )}
          </button>
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

    </div>
  );
};

export default DeleteWorkspaceModal;
